import { useState } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";

export default function QRCodeGenerator({
  link,
  show,
  title = "store-menu-qr-code",
  includeCompanyDetails = false,
  warehouseName = "",
  companyName = "",
  companyLogo = "",
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { backendUrl } = useAuth();

  const loadImage = (src, useCors = false) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const downloadQRCode = async () => {
    try {
      setIsDownloading(true);

      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
        link,
      )}`;

      const [qrResponse, logoImg] = await Promise.all([
        fetch(qrUrl).then((r) => r.blob()),
        companyLogo ? loadImage(companyLogo, true) : Promise.resolve(null),
      ]);

      const qrObjectUrl = URL.createObjectURL(qrResponse);
      const qrImg = await loadImage(qrObjectUrl);
      const qrSize = 300;
      const padding = 10;
      const logoHeight = 30;
      const logoTextGap = 8;
      const headerHeight = padding + logoHeight + padding; // logo+name on one line
      const footerHeight = 50;
      const canvasWidth = qrSize + padding * 2;
      const canvasHeight = headerHeight + qrSize + footerHeight + padding * 2;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");

      // background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // --- header: logo + company name, side by side on one line ---
      const headerY = padding;
      const fontStyle = "bold 12px Arial, sans-serif";

      let logoWidth = 30;
      if (logoImg) {
        logoWidth = logoWidth;
      }
      ctx.font = fontStyle;
      const textWidth = companyName ? ctx.measureText(companyName).width : 0;

      const gap = logoImg && companyName ? logoTextGap : 0;
      const blockWidth = logoWidth + gap + textWidth;
      const blockStartX = (canvasWidth - blockWidth) / 2;
      const lineCenterY = headerY + logoHeight / 2;

      let cursorX = blockStartX;

      if (logoImg) {
        ctx.drawImage(logoImg, cursorX, headerY, logoWidth, logoHeight);
        cursorX += logoWidth + gap;
      }

      if (companyName) {
        ctx.fillStyle = "#000000";
        ctx.font = fontStyle;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(companyName, cursorX, lineCenterY);
        ctx.textBaseline = "alphabetic"; // reset for later text (footer)
      }

      // --- QR code ---
      const qrY = headerHeight + padding;
      ctx.drawImage(qrImg, padding, qrY, qrSize, qrSize);

      // --- footer: warehouse name ---
      if (warehouseName) {
        ctx.fillStyle = "#000000";
        ctx.font = "14px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(
          warehouseName,
          canvasWidth / 2,
          qrY + qrSize + footerHeight / 2 + 5,
        );
      }

      // --- export canvas as the downloaded file ---
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link_element = document.createElement("a");
        link_element.href = url;
        link_element.download = `${title}-${Date.now()}.png`;

        document.body.appendChild(link_element);
        link_element.click();
        document.body.removeChild(link_element);

        URL.revokeObjectURL(url);
        URL.revokeObjectURL(qrObjectUrl);
        setIsDownloading(false);
        toast.success("QR code downloaded successfully!");
      }, "image/png");
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast.error("Failed to download QR code. Please try again.");
      setIsDownloading(false);
    }
  };
  return (
    <div className="my-0">
      <div
        style={{
          display: show ? "flex" : "none",
          width: "100px",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${link}`}
          alt="qr-code"
          style={{ width: "100px", height: "100px" }}
        />
        <button onClick={downloadQRCode} className=" mt-1" type="button">
          {isDownloading ? "Downloading..." : "Download"}
        </button>
      </div>
    </div>
  );
}
